// supabase.functions.invoke throws on any non-2xx BEFORE parsing the body, and
// its message is always the generic "Edge Function returned a non-2xx status
// code". The server's real reason (NO_REVIEWABLE_REQUESTS,
// INVALID_VALIDITY_RANGE, "column profiles_2.app_language does not exist", …)
// only survives on the raw Response hung off error.context — so every media
// mutation that invokes an edge function reads it through this helper instead of
// showing the generic string to a league admin.

/** Shape the media edge functions return on failure: `{ error, message? }`. */
interface InvokeErrorBody {
    error?: unknown;
    message?: unknown;
}

/**
 * Best human-readable message for a failed `functions.invoke`.
 * Prefers the body's `message` (the sentence), then its `error` code, then the
 * generic client message. Never throws: a body that is empty, non-JSON, or
 * already consumed just falls through.
 */
export async function messageFromInvokeError(error: unknown): Promise<string> {
    const err = error as
        | { message?: string; context?: { json?: () => Promise<unknown> } }
        | null
        | undefined;

    try {
        const body = (await err?.context?.json?.()) as InvokeErrorBody | null;
        if (body?.message) return String(body.message);
        if (body?.error) return String(body.error);
    } catch {
        // body not JSON / already consumed — fall through to the generic message
    }

    return err?.message ?? "Request failed";
}

/**
 * Same precedence for a 2xx response that carries an error envelope in its body
 * (several media functions answer 200 with `{ error, message }`).
 */
export function messageFromInvokeBody(data: unknown): string | null {
    const body = data as InvokeErrorBody | null;
    if (!body?.error) return null;
    return String(body.message ?? body.error);
}

// ----------------------------------------------------------------------------
// Code-aware variant. The org-only functions (review-league-access,
// review-media-profile, ...) answer `{ error: <CODE>, message }`, some older
// ones `{ error: <sentence>, code }`. Keeping the code lets the UI show a
// localized message (media.json → api_errors.<CODE>) and fall back on the
// server's sentence for a code it does not know yet.
// ----------------------------------------------------------------------------

const CODE_PATTERN = /^[A-Z][A-Z0-9_]+$/;

/** A failed media edge-function call: machine `code` (or null) + best message. */
export class MediaFunctionError extends Error {
    readonly code: string | null;

    constructor(code: string | null, message: string) {
        super(message);
        this.name = "MediaFunctionError";
        this.code = code;
    }
}

function codeFromBody(body: (InvokeErrorBody & { code?: unknown }) | null | undefined): string | null {
    if (typeof body?.code === "string" && CODE_PATTERN.test(body.code)) return body.code;
    if (typeof body?.error === "string" && CODE_PATTERN.test(body.error)) return body.error;
    return null;
}

function codeFromStatus(status: unknown): string | null {
    if (status === 401) return "UNAUTHORIZED";
    if (status === 403) return "ACCESS_DENIED";
    return null;
}

/** Same as messageFromInvokeError, but keeps the code. Never throws. */
export async function mediaErrorFromInvoke(error: unknown): Promise<MediaFunctionError> {
    const err = error as
        | { message?: string; context?: { status?: number; json?: () => Promise<unknown> } }
        | null
        | undefined;

    let body: (InvokeErrorBody & { code?: unknown }) | null = null;
    try {
        body = ((await err?.context?.json?.()) ?? null) as typeof body;
    } catch {
        // not JSON / already consumed
    }

    const code = codeFromBody(body) ?? codeFromStatus(err?.context?.status);
    const message =
        (body?.message && String(body.message)) ||
        (body?.error && String(body.error)) ||
        err?.message ||
        "Request failed";
    return new MediaFunctionError(code, message);
}

/** A 2xx response carrying an error envelope, or null when it is a success. */
export function mediaErrorFromBody(data: unknown): MediaFunctionError | null {
    const body = data as (InvokeErrorBody & { code?: unknown }) | null;
    if (!body?.error) return null;
    return new MediaFunctionError(codeFromBody(body), String(body.message ?? body.error));
}

type Translate = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Message to show a league admin: the localized `api_errors.<CODE>` text when
 * the code is known, else the server's sentence. `t` must be bound to the
 * `media` namespace.
 */
export function localizeMediaError(t: Translate, error: unknown): string {
    if (error instanceof MediaFunctionError) {
        return error.code
            ? t(`api_errors.${error.code}`, { defaultValue: error.message })
            : error.message;
    }
    if (error instanceof Error) return error.message;
    return String(error);
}
